import AccreditationFields from '@components/Card2/CardFormFields/AccreditationsFields/AccreditationsFields';
import { canDeleteCardItem } from '@components/Card2/utils';
import { cardEditContext } from '@context/card-edit-context';
import { cardPreviewContext } from '@context/card-preview-context';
import { useCardAttribute } from '@hooks/use-card-attributes';
import { useCardPropertyState } from '@hooks/use-card-property-state';
import { useCallback, useContext, useMemo, useState } from 'react';

import { CardItemEditor } from '../../CardItemEditor';
import { CardProperty } from '../CardProperty';
import { useCardListPropertyMutations } from '../hooks/card-list-property-mutations';
import styles from './Accreditations.module.scss';
import type { CardListProperty, ValueProvider } from '.cache/__types__';

type AccreditationsProps = {
  property?: CardListProperty;
  // In future, cardId could come from the cardFieldEditContext (or similar context), that way we can tell if there is no
  // cardId, that we need to create a new card before adding the property.
  cardId: string;
};

export const Accreditations: React.FC<AccreditationsProps> = ({ property, cardId }) => {
  const [tempValueProviderForTour, setTempValueProviderForTour] = useState<ValueProvider | null>(null);

  const { activeAttributeId, activeAttribute, flavour } = useContext(cardEditContext);
  const { hideEmptyAttributes } = useContext(cardPreviewContext);

  const cardAttributeType = useCardAttribute('accreditations');

  const propertyState = useCardPropertyState<string[]>(
    property,
    cardAttributeType?.id,
    activeAttributeId,
    'member',
  );

  const { create, update, remove } = useCardListPropertyMutations(cardId);

  const isFormStateValid = useMemo(() => {
    // Template creators can still assign members to create accreditations
    if (propertyState.value === null && propertyState.valueProvider === 'member') return true;

    // If the array of accreditations is empty, don't allow saving
    if (!propertyState.value?.length) return false;

    // Check every item in the list is populated, but not just an empty string
    return propertyState.value?.every((accreditation) => !!accreditation && accreditation !== '');
  }, [propertyState.value, propertyState.valueProvider]);

  const onSave = useCallback(() => {
    // Check whether the accreditation form is valid before firing mutation.
    if (!isFormStateValid) {
      return;
    }

    const stage = propertyState.value && propertyState.value.length > 0 ? 'done' : 'todo';

    const sharedMutationValues: Omit<CardListProperty, 'id' | 'attribute_type_id'> & {
      card_id: string;
    } = {
      card_id: cardId,
      instruction_text: propertyState.instruction ?? null,
      is_optional: propertyState.isOptional,
      value_provider: propertyState.valueProvider,
      value_locked: propertyState.valueLocked,
      values: propertyState.value,
      template_id: property?.template_id,
      stage,
    };

    if (!property) {
      if (!activeAttribute) {
        return;
      }

      create(
        {
          attribute_type_id: activeAttribute?.id,
          ...sharedMutationValues,
        },
        activeAttribute,
      );

      return;
    }

    if (property.attribute_type) {
      update(
        {
          id: property.id,
          ...sharedMutationValues,
        },
        property.attribute_type,
      );
    }
  }, [
    activeAttribute,
    cardId,
    create,
    isFormStateValid,
    property,
    propertyState.instruction,
    propertyState.isOptional,
    propertyState.value,
    propertyState.valueLocked,
    propertyState.valueProvider,
    update,
  ]);

  const canDelete = canDeleteCardItem({
    flavour,
    isOptional: Boolean(property?.is_optional),
    isTemplated: Boolean(property?.template_id),
    valueProvider: property?.value_provider,
  });

  const onDelete = useCallback(() => {
    if (!property || !canDelete) {
      return;
    }
    remove(cardId, property.id, property?.attribute_type?.name ?? undefined);
  }, [cardId, property, remove, canDelete]);

  if (!propertyState.showProperty) {
    return null;
  }

  // if hideEmptyAttributes specified as true and no accreditations, then accreditations shouldn't be shown on the card at all
  if (hideEmptyAttributes && property?.values?.length === 0) {
    return <></>;
  }

  return (
    <div className={styles['user-accreditations-container']}>
      <CardProperty
        icon={null}
        trackingAttributeName={cardAttributeType?.name || 'accreditations'}
        updateTempValueProviderForTour={setTempValueProviderForTour}
        isOptional={propertyState.isOptional}
        attributeDisplayName={cardAttributeType?.display_name || ''}
        editUI={
          <CardItemEditor {...propertyState} tempValueProviderForTour={tempValueProviderForTour}>
            <AccreditationFields
              field={propertyState.value?.map((accreditation) => accreditation) ?? []}
              onChange={(newAccreditations) => {
                propertyState.setValue(newAccreditations);
              }}
              valueLocked={propertyState.valueLocked ?? false}
              toggleValueLocked={() => propertyState.setValueLocked?.(!propertyState.valueLocked)}
              valueLockEnabled={propertyState.valueLockEnabled}
            />
          </CardItemEditor>
        }
        canDelete={canDelete}
        onDelete={onDelete}
        validator={() => isFormStateValid}
        attributeId={cardAttributeType?.id ?? ''}
        positionDeps={[propertyState.value?.length]}
        onSave={onSave}
        onCancel={propertyState.onCancel}
        isLocked={propertyState.fullyLocked}
        isLockedTooltipMessage={propertyState.lockedTooltipMessage}
        attributeValue={property?.values && property?.values.length > 0 ? 'card has accreditations' : ''}
      >
        <ul className={styles['user-accreditations']}>
          {!propertyState.showPropertyPlaceholder &&
            propertyState.value?.map((accreditation, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: historic
              <li className={styles['accreditation']} key={i}>
                {accreditation}
              </li>
            ))}
          {propertyState.showPropertyPlaceholder && (
            <li className={styles['accreditation']} data-placeholder={true}>
              Accreditation
            </li>
          )}
        </ul>
      </CardProperty>
    </div>
  );
};
